'use client';

/**
 * RankProgress - Current rank with progress bar to next tier
 */

import React from 'react';
import { Crown } from 'lucide-react';
import { GlassCard, CardContent } from '@/components/ui/glass-card';
import type { ReferralTier } from '../types';

interface RankProgressProps {
  currentTier: ReferralTier;
  nextTier: ReferralTier | null;
  progressPercent: number;
  totalReferrals: number;
}

// Badge color mapping
const badgeColors: Record<string, { bg: string; text: string; border: string }> = {
  gray: {
    bg: 'bg-gray-100 dark:bg-white/[0.03]',
    text: 'text-gray-700 dark:text-gray-300',
    border: 'border-gray-300 dark:border-gray-600',
  },
  silver: {
    bg: 'bg-slate-100 dark:bg-slate-800',
    text: 'text-slate-700 dark:text-slate-300',
    border: 'border-slate-400 dark:border-slate-500',
  },
  gold: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    border: 'border-amber-400 dark:border-amber-500',
  },
  white: {
    bg: 'bg-gradient-to-r from-amber-50 to-white dark:from-amber-900/20 dark:to-white/10',
    text: 'text-amber-800 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-400',
  },
};

export function RankProgress({ currentTier, nextTier, progressPercent, totalReferrals }: RankProgressProps) {
  const currentColors = badgeColors[currentTier.badge_color] || badgeColors.gray;
  const nextColors = nextTier ? badgeColors[nextTier.badge_color] || badgeColors.gray : null;

  // Calculate tier markers for the progress bar
  const tiers = ['affiliate', 'agent', 'associate', 'partner'];
  const tierDisplayNames: Record<string, string> = {
    affiliate: 'Affiliate',
    agent: 'Agent (Silver)',
    associate: 'Associate (Gold)',
    partner: 'Partner (White Gold)',
  };

  return (
    <GlassCard className="overflow-hidden">
      <CardContent className="p-4 sm:p-6">
        {/* Header with Current and Next Rank */}
        <div className="flex items-center justify-between mb-6">
          {/* Current Rank */}
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              Current Rank
            </p>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                {currentTier.display_name.split(' ')[0]}
              </h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${currentColors.bg} ${currentColors.text} ${currentColors.border}`}>
                {currentTier.commission_percent}% Cut
              </span>
            </div>
          </div>

          {/* Next Rank */}
          {nextTier && nextColors && (
            <div className="text-right">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                Next Rank
              </p>
              <div className="flex items-center gap-2 justify-end">
                <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                  {nextTier.display_name.split(' ')[0]}
                </span>
                <Crown className="h-5 w-5 text-amber-500" />
              </div>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="relative">
          {/* Background Track */}
          <div className="h-2 bg-gray-200 dark:bg-white/5 rounded-full overflow-hidden">
            {/* Progress Fill */}
            <div
              className="h-full bg-gradient-to-r from-brand-primary to-brand-accent rounded-full transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Progress Indicator */}
          <div
            className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-white/[0.02] border-2 border-brand-primary rounded-full shadow-md transition-all duration-500"
            style={{ left: `calc(${progressPercent}% - 8px)` }}
          />
        </div>

        {/* Tier Labels */}
        <div className="flex justify-between mt-4 text-xs text-gray-500 dark:text-gray-400">
          {tiers.map((tierName, index) => {
            const isActive = currentTier.name === tierName;
            const isPast = tiers.indexOf(currentTier.name) > index;

            return (
              <div
                key={tierName}
                className={`text-center ${
                  isActive
                    ? 'text-brand-primary font-medium'
                    : isPast
                    ? 'text-gray-600 dark:text-gray-400'
                    : 'text-gray-400 dark:text-gray-600'
                }`}
              >
                {tierDisplayNames[tierName]}
              </div>
            );
          })}
        </div>
      </CardContent>
    </GlassCard>
  );
}
